<script>
import { ref } from 'vue';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';

export default {
  components: { Avatar },
  props: {
    group: {
      type: Object,
      default: () => ({}),
    },
    inboxId: {
      type: [Number, String],
      default: null,
    },
    accessToken: {
      type: String,
      default: '',
    },
  },
  emits: ['groupUpdated'],
  setup() {
    const showSearchModal = ref(false);
    const showParticipantModal = ref(false);
    const showGroupToolsModal = ref(false);
    const searchQuery = ref('');
    const selectedParticipant = ref(null);

    return {
      showSearchModal,
      showParticipantModal,
      showGroupToolsModal,
      searchQuery,
      selectedParticipant,
    };
  },
  computed: {
    participants() {
      return Array.isArray(this.group.participants)
        ? this.group.participants
        : [];
    },
    participantCount() {
      return Number(this.group.participants_count || this.participants.length);
    },
    participantCountLabel() {
      return `${this.participantCount} membros`;
    },
    searchMembersLabel() {
      return 'Pesquisar membros';
    },
    emptyMembersLabel() {
      return 'Nenhum membro recebido para este grupo.';
    },
    closeModalLabel() {
      return 'Fechar pesquisa de membros';
    },
    searchContactsPlaceholder() {
      return 'Pesquisar contatos';
    },
    noMembersFoundLabel() {
      return 'Nenhum membro encontrado.';
    },
    promoteAdminLabel() {
      return 'Promover a admin do grupo';
    },
    removeMemberLabel() {
      return 'Remover';
    },
    demoteAdminLabel() {
      return 'Remover como admin';
    },
    addMemberLabel() {
      return 'Adicionar membro';
    },
    groupToolsLabel() {
      return 'Configurações do grupo';
    },
    contactDetailsLabel() {
      return 'Dados do contato';
    },
    filteredParticipants() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.participants;

      return this.participants.filter(participant => {
        const name = this.participantName(participant).toLowerCase();
        const phone = this.participantPhone(participant).toLowerCase();
        return name.includes(query) || phone.includes(query);
      });
    },
    selectedParticipantName() {
      return this.selectedParticipant
        ? this.participantName(this.selectedParticipant)
        : '';
    },
    canManageGroup() {
      return (
        this.group.current_user_is_admin === true ||
        this.group.current_user_is_admin === 'true'
      );
    },
    selectedParticipantIsAdmin() {
      return Boolean(
        this.selectedParticipant &&
          (this.selectedParticipant.is_admin ||
            this.selectedParticipant.admin ||
            ['owner', 'admin'].includes(this.selectedParticipant.role))
      );
    },
    canManageSelectedParticipant() {
      return this.canManageGroup && !this.selectedParticipant?.is_me;
    },
  },
  methods: {
    participantKey(participant, index) {
      if (typeof participant === 'string') return participant;
      return (
        participant.id ||
        participant.phone_number ||
        `${participant.name || 'member'}-${index}`
      );
    },
    participantName(participant) {
      if (typeof participant === 'string')
        return participant.replace(/@.*/, '');
      return (
        participant.name ||
        (participant.phone_number ? `+${participant.phone_number}` : '') ||
        (participant.id || '').replace(/@.*/, '')
      );
    },
    participantAvatar(participant) {
      if (typeof participant === 'string') return '';
      return (
        participant.thumbnail ||
        participant.avatar_url ||
        participant.profile_pic_url ||
        participant.picture ||
        participant.image_url ||
        ''
      );
    },
    participantPhone(participant) {
      if (typeof participant === 'string' || !participant.phone_number)
        return '';
      return `+${participant.phone_number}`;
    },
    participantRoleBadge(participant) {
      if (typeof participant === 'string') return '';
      if (participant.is_owner || participant.role === 'owner')
        return 'Admin do grupo';
      if (
        participant.is_admin ||
        participant.admin ||
        participant.role === 'admin'
      ) {
        return 'Admin do grupo';
      }
      return '';
    },
    participantSecondaryText(participant) {
      if (typeof participant === 'string') return '';
      return (
        participant.about ||
        participant.description ||
        participant.status ||
        this.participantPhone(participant)
      );
    },
    openSearchModal() {
      this.searchQuery = '';
      this.selectedParticipant = null;
      this.showSearchModal = true;
    },
    closeSearchModal() {
      this.showSearchModal = false;
    },
    openParticipantActions(participant) {
      this.selectedParticipant = participant;
      this.showSearchModal = false;
      this.showParticipantModal = true;
    },
    closeParticipantActions() {
      this.selectedParticipant = null;
      this.showParticipantModal = false;
    },
    groupApiUrl() {
      return `https://homolog.aceleres.ai/api/wame/groups/${this.inboxId}`;
    },
    groupApiHeaders() {
      return {
        'X-Chatwoot-Token': this.accessToken,
        'X-Chatwoot-Account-Id': this.$route.params.accountId || '',
      };
    },
    participantIdentifier(participant) {
      return participant?.id || participant?.phone_number || '';
    },
    async updateParticipants(action, participants) {
      if (!this.group.jid || !this.inboxId) return;
      try {
        const response = await axios.post(
          `${this.groupApiUrl()}/participants`,
          { group_jid: this.group.jid, action, participants },
          { headers: this.groupApiHeaders() }
        );
        this.$emit('groupUpdated', response.data.group);
        useAlert('Grupo atualizado no WhatsApp.');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível atualizar os participantes do grupo.'
        );
      }
    },
    async updateSelectedParticipant(action) {
      const identifier = this.participantIdentifier(this.selectedParticipant);
      if (!identifier) return;
      await this.updateParticipants(action, [identifier]);
      this.closeParticipantActions();
    },
    async addParticipant() {
      const raw = window.prompt('Informe o número com DDI e DDD (somente dígitos).');
      const phone = String(raw || '').replace(/\D/g, '');
      if (!phone) return;
      await this.updateParticipants('add', [`${phone}@s.whatsapp.net`]);
    },
    async refreshGroup() {
      if (!this.group.jid || !this.inboxId) return;
      try {
        const response = await axios.get(this.groupApiUrl(), {
          headers: this.groupApiHeaders(),
          params: { group_jid: this.group.jid },
        });
        this.$emit('groupUpdated', response.data.group);
      } catch {
        // A cache já exibida continua útil se a consulta remota oscilar.
      }
    },
    async updateGroupSetting(setting) {
      if (!this.group.jid || !this.inboxId) return;
      try {
        const response = await axios.patch(
          `${this.groupApiUrl()}/settings`,
          { group_jid: this.group.jid, setting },
          { headers: this.groupApiHeaders() }
        );
        this.$emit('groupUpdated', response.data.group);
        useAlert('Configuração do grupo atualizada no WhatsApp.');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível atualizar a configuração do grupo.'
        );
      }
    },
    async copyInviteLink() {
      try {
        const response = await axios.get(`${this.groupApiUrl()}/invite`, {
          headers: this.groupApiHeaders(),
          params: { group_jid: this.group.jid },
        });
        const invite = response.data.invite || {};
        const code = invite.code || invite.inviteCode || invite.data?.code;
        const link =
          invite.link ||
          invite.inviteLink ||
          invite.data?.link ||
          (code ? `https://chat.whatsapp.com/${code}` : '');
        if (!link) throw new Error('Link não recebido');
        await navigator.clipboard.writeText(link);
        useAlert('Link de convite copiado.');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível obter o link de convite do grupo.'
        );
      }
    },
  },
  mounted() {
    this.refreshGroup();
  },
};
</script>

<template>
  <section
    id="contact-group-members"
    class="contact-details-group"
    aria-labelledby="contact-group-members-title"
  >
    <header class="contact-details-group__header">
      <h3 id="contact-group-members-title" class="contact-details-group__title">
        {{ participantCountLabel }}
      </h3>
      <div class="flex items-center gap-1">
        <!--
        <button
          v-if="canManageGroup"
          type="button"
          class="contact-details-group__search"
          :aria-label="addMemberLabel"
          @click="addParticipant"
        >
          <span class="i-lucide-user-plus" aria-hidden="true" />
        </button>
        <button
          v-if="canManageGroup"
          type="button"
          class="contact-details-group__search"
          :aria-label="groupToolsLabel"
          @click="showGroupToolsModal = true"
        >
          <span class="i-lucide-settings-2" aria-hidden="true" />
        </button>
        -->
        <button
          type="button"
          class="contact-details-group__search"
          :aria-label="searchMembersLabel"
          @click="openSearchModal"
        >
          <span class="i-lucide-search" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="contact-details-group__content">
      <div v-if="participants.length" class="contact-details-group__members">
        <div
          v-for="(participant, index) in participants"
          :key="participantKey(participant, index)"
          class="contact-details-group__member hover:rounded-lg hover:bg-n-slate-2 hover:outline hover:outline-1 hover:outline-n-weak hover:cursor-grab"
          @click="openSearchModal"
        >
          <Avatar
            :src="participantAvatar(participant)"
            :name="participantName(participant)"
            :size="52"
            rounded-full
            hide-offline-status
            class="contact-details-group__member-avatar"
          />
          <div class="contact-details-group__member-text">
            <span class="contact-details-group__member-name">{{
              participantName(participant)
            }}</span>
            <span
              v-if="participantSecondaryText(participant)"
              class="contact-details-group__member-phone"
            >
              {{ participantSecondaryText(participant) }}
            </span>
          </div>
          <span
            v-if="participantRoleBadge(participant)"
            class="contact-details-group__member-role"
          >
            {{ participantRoleBadge(participant) }}
          </span>
        </div>
      </div>
      <p v-else class="contact-details-group__empty">
        {{ emptyMembersLabel }}
      </p>
    </div>

    <woot-modal
      v-model:show="showSearchModal"
      :on-close="closeSearchModal"
      :show-close-button="false"
      class="contact-group-members-modal"
    >
      <section class="contact-group-members-modal__content">
        <header class="contact-group-members-modal__header">
          <button
            type="button"
            class="contact-group-members-modal__close"
            :aria-label="closeModalLabel"
            @click="closeSearchModal"
          >
            <span class="i-lucide-x" aria-hidden="true" />
          </button>
          <h3>{{ searchMembersLabel }}</h3>
        </header>

        <label class="contact-group-members-modal__search acl-search-pill">
          <span
            class="i-lucide-search acl-search-pill__icon"
            aria-hidden="true"
          />
          <input
            v-model="searchQuery"
            type="search"
            class="acl-search-pill__input"
            :placeholder="searchContactsPlaceholder"
            :aria-label="searchContactsPlaceholder"
          />
        </label>

        <div class="contact-group-members-modal__list">
          <button
            v-for="(participant, index) in filteredParticipants"
            :key="participantKey(participant, index)"
            type="button"
            class="contact-group-members-modal__member"
            :class="{
              'contact-group-members-modal__member--selected':
                selectedParticipant === participant,
            }"
            @click="openParticipantActions(participant)"
          >
            <Avatar
              :src="participantAvatar(participant)"
              :name="participantName(participant)"
              :size="52"
              rounded-full
              hide-offline-status
              class="contact-group-members-modal__member-avatar"
            />
            <span class="contact-group-members-modal__member-details">
              <span class="contact-group-members-modal__member-name">
                {{ participantName(participant) }}
              </span>
              <span
                v-if="participantSecondaryText(participant)"
                class="contact-group-members-modal__member-secondary"
              >
                {{ participantSecondaryText(participant) }}
              </span>
            </span>
            <span
              v-if="participantRoleBadge(participant)"
              class="contact-group-members-modal__member-role"
            >
              {{ participantRoleBadge(participant) }}
            </span>
          </button>
          <p
            v-if="!filteredParticipants.length"
            class="contact-group-members-modal__empty"
          >
            {{ noMembersFoundLabel }}
          </p>
        </div>
      </section>
    </woot-modal>

    <woot-modal
      v-model:show="showGroupToolsModal"
      :show-close-button="false"
      class="contact-group-member-modal"
    >
      <section class="contact-group-member-modal__content">
        <header class="contact-group-member-modal__header">
          <h3>{{ groupToolsLabel }}</h3>
          <button
            type="button"
            class="contact-group-member-modal__close"
            :aria-label="closeModalLabel"
            @click="showGroupToolsModal = false"
          >
            <span class="i-lucide-x" aria-hidden="true" />
          </button>
        </header>
        <div class="contact-group-member-modal__actions">
          <button type="button" @click="updateGroupSetting('announcement')">
            <span class="i-lucide-megaphone" aria-hidden="true" />
            <span>Somente admins enviam mensagens</span>
          </button>
          <button type="button" @click="updateGroupSetting('not_announcement')">
            <span class="i-lucide-message-circle" aria-hidden="true" />
            <span>Todos podem enviar mensagens</span>
          </button>
          <button type="button" @click="updateGroupSetting('locked')">
            <span class="i-lucide-lock" aria-hidden="true" />
            <span>Somente admins alteram dados</span>
          </button>
          <button type="button" @click="updateGroupSetting('unlocked')">
            <span class="i-lucide-unlock" aria-hidden="true" />
            <span>Todos podem alterar dados</span>
          </button>
          <button type="button" @click="copyInviteLink">
            <span class="i-lucide-link" aria-hidden="true" />
            <span>Copiar link de convite</span>
          </button>
        </div>
      </section>
    </woot-modal>

    <woot-modal
      v-model:show="showParticipantModal"
      :on-close="closeParticipantActions"
      :show-close-button="false"
      class="contact-group-member-modal"
    >
      <section
        v-if="selectedParticipant"
        class="contact-group-member-modal__content"
      >
        <header class="contact-group-member-modal__header">
          <button
            type="button"
            class="contact-group-member-modal__close"
            :aria-label="closeModalLabel"
            @click="closeParticipantActions"
          >
            <span class="i-lucide-x" aria-hidden="true" />
          </button>
          <Avatar
            :src="participantAvatar(selectedParticipant)"
            :name="selectedParticipantName"
            :size="64"
            rounded-full
            hide-offline-status
          />
          <div class="contact-group-member-modal__identity">
            <h3>{{ selectedParticipantName }}</h3>
            <span v-if="participantSecondaryText(selectedParticipant)">
              {{ participantSecondaryText(selectedParticipant) }}
            </span>
          </div>
        </header>

        <div class="contact-group-member-modal__actions">
          <button
            v-if="canManageSelectedParticipant && !selectedParticipantIsAdmin"
            type="button"
            @click="updateSelectedParticipant('promote')"
          >
            <span class="i-lucide-user-round-cog" aria-hidden="true" />
            <span>{{ promoteAdminLabel }}</span>
          </button>
          <button
            v-if="canManageSelectedParticipant && selectedParticipantIsAdmin && selectedParticipant.role !== 'owner'"
            type="button"
            @click="updateSelectedParticipant('demote')"
          >
            <span class="i-lucide-user-round-x" aria-hidden="true" />
            <span>{{ demoteAdminLabel }}</span>
          </button>
          <button
            v-if="canManageSelectedParticipant"
            type="button"
            @click="updateSelectedParticipant('remove')"
          >
            <span class="i-lucide-circle-minus" aria-hidden="true" />
            <span>{{ removeMemberLabel }}</span>
          </button>
          <button type="button" @click="closeParticipantActions">
            <span class="i-lucide-circle-user-round" aria-hidden="true" />
            <span>{{ contactDetailsLabel }}</span>
          </button>
          <button type="button" @click="closeParticipantActions">
            <span class="i-lucide-message-circle" aria-hidden="true" />
            <span>{{ `Conversar com ${selectedParticipantName}` }}</span>
          </button>
        </div>
      </section>
    </woot-modal>
  </section>
</template>

<style scoped>
.contact-details-group {
  width: 100%;
  overflow: hidden;
  background: #fff;
  color: #111827;
}

.contact-details-group__header {
  display: flex;
  min-height: 54px;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 0 30px;
  background: #fff;
}

.contact-details-group__title {
  margin: 0;
  color: #1f2937;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
}

.contact-details-group__search {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #111827;
  cursor: pointer;
}

.contact-details-group__search:hover,
.contact-details-group__search:focus-visible {
  background: #f1f5f9;
  outline: none;
}

.contact-details-group__search > span {
  width: 20px;
  height: 20px;
}

.contact-details-group__content {
  padding: 4px 20px 12px 30px;
  font-size: 0.95rem;
}

.contact-details-group__member {
  display: flex;
  min-height: 76px;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  outline: 1px solid transparent;
  text-align: left;
  transition:
    background-color 160ms ease,
    outline-color 160ms ease;
}

.contact-details-group__member-avatar {
  flex-shrink: 0;
}

.contact-details-group__member-text {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}

.contact-details-group__member-name,
.contact-details-group__member-phone {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-group__member-name {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 600;
}

.contact-details-group__member-phone {
  color: #7a7f86;
  font-size: 0.825rem;
}

.contact-details-group__member-role {
  flex-shrink: 0;
  padding: 5px 8px;
  border-radius: 6px;
  background: #d9fbdc;
  color: #18864b;
  font-size: 0.6875rem;
  font-weight: 700;
}

.contact-details-group__empty {
  margin: 0;
  padding: 18px 0;
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
}

.contact-group-members-modal__content {
  box-sizing: border-box;
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  padding: 28px 32px 32px;
  border-radius: 24px;
  background: #fff;
  color: #111827;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.contact-group-members-modal__header {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 22px;
  height: auto;
  padding: 0;
  margin-bottom: 20px;
}

.contact-group-members-modal__header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
}

.contact-group-members-modal__close {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  font-size: 28px;
}

.contact-group-members-modal__close:hover,
.contact-group-members-modal__close:focus-visible {
  background: transparent;
  outline: none;
}

.contact-group-members-modal__close > span {
  width: 28px;
  height: 28px;
}

.contact-group-members-modal__search {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
  padding: 5px 14px;
  border: none;
  border-radius: 50px;
  background: rgba(194, 189, 184, 0.15);
  color: #8a8a8a;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow 0.12s;
}

.contact-group-members-modal__search:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}

.contact-group-members-modal__search:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

.contact-group-members-modal__search .acl-search-pill__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}

.contact-group-members-modal__search input {
  box-sizing: border-box;
  min-width: 0;
  flex: 1;
  width: 100%;
  height: 40px;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #1f2937;
  font-size: 13px;
  line-height: 40px;
  box-shadow: none;
}

.contact-group-members-modal__search input::placeholder {
  color: #8a8a8a;
}

.contact-group-members-modal__search input:focus,
.contact-group-members-modal__search input:focus-visible {
  border: none;
  outline: none;
  background: transparent;
  box-shadow: none;
}

.contact-group-members-modal__list {
  width: 100%;
  min-width: 0;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
}

.contact-group-members-modal__list::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.contact-group-members-modal__member {
  display: flex;
  box-sizing: border-box;
  min-height: 82px;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 18px;
  width: 100%;
  padding: 8px 4px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #111827;
  cursor: pointer;
  text-align: left;
}

.contact-group-members-modal__member:hover,
.contact-group-members-modal__member--selected {
  background: #f8fafc;
}

.contact-group-members-modal__member-details {
  display: flex;
  overflow: hidden;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.contact-group-members-modal__member-name,
.contact-group-members-modal__member-secondary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-group-members-modal__member-name {
  color: #1f2937;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.35;
}

.contact-group-members-modal__member-secondary {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.35;
}

.contact-group-members-modal__member-role {
  max-width: 35%;
  overflow: hidden;
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 9999px;
  background: #d9fbdc;
  color: #18864b;
  font-size: 0.75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-group-members-modal__empty {
  margin: 24px 0;
  color: #64748b;
  text-align: center;
}

.contact-group-member-modal__content {
  width: min(420px, calc(100vw - 32px));
  padding: 24px 0 12px;
  border-radius: 18px;
  background: #fff;
  color: #111827;
}

.contact-group-member-modal__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
}

.contact-group-member-modal__close {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  margin-right: -4px;
  border: 0;
  background: transparent;
  color: #111827;
  cursor: pointer;
}

.contact-group-member-modal__identity {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.contact-group-member-modal__identity h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.contact-group-member-modal__identity span {
  overflow: hidden;
  color: #64748b;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-group-member-modal__actions {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px 0 0;
  border-top: 1px solid #e2e8f0;
}

.contact-group-member-modal__actions button {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 54px;
  padding: 0 24px;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 1rem;
  text-align: left;
}

.contact-group-member-modal__actions button:hover {
  background: #f8fafc;
}

.contact-group-member-modal__actions button > span:first-child {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

:global(.modal-mask.contact-group-members-modal),
:global(.modal-mask:has(.contact-group-member-modal)) {
  background: rgba(15, 23, 42, 0.55) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

:global(.modal-mask.contact-group-members-modal .modal-container),
:global(.modal-mask:has(.contact-group-member-modal) .modal-container) {
  background: #fff !important;
}

:global(.modal-mask.contact-group-members-modal) {
  overflow: hidden !important;
  padding: 16px !important;
}

:global(.modal-mask.contact-group-members-modal .modal-container) {
  width: min(656px, calc(100vw - 32px)) !important;
  max-width: calc(100vw - 32px) !important;
  height: min(760px, calc(100vh - 32px)) !important;
  max-height: calc(100vh - 32px) !important;
  overflow: hidden !important;
  border-radius: 2rem !important;
  background: #fff !important;
  padding: 0 !important;
}

@media (max-width: 640px) {
  .contact-group-members-modal__content {
    width: calc(100vw - 2rem);
    max-width: calc(100vw - 2rem);
    height: calc(100vh - 2rem);
    max-height: calc(100vh - 2rem);
    padding: 20px 16px 24px;
    border-radius: 1.5rem;
  }

  .contact-group-members-modal__header {
    gap: 12px;
    margin-bottom: 16px;
  }

  .contact-group-members-modal__header h3 {
    overflow: hidden;
    font-size: 1.125rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .contact-group-members-modal__search {
    margin-bottom: 16px;
  }

  .contact-group-members-modal__member {
    min-height: 76px;
    gap: 12px;
    padding: 8px;
  }

  .contact-group-members-modal__member-name {
    font-size: 1rem;
  }

  .contact-group-members-modal__member-secondary {
    font-size: 0.875rem;
  }

  .contact-group-members-modal__member-role {
    max-width: 38%;
    padding: 5px 8px;
    font-size: 0.75rem;
  }
}
</style>
